import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbCalendar, NgbDate, NgbDatepicker, NgbDatepickerModule } from '@ng-bootstrap/ng-bootstrap';

export interface DateTimeSelection {
  date: NgbDate;
  time: string;
  dateTime: Date;
}

@Component({
  selector: 'app-datetime-picker',
  standalone: true,
  imports: [CommonModule, FormsModule, NgbDatepickerModule],
  templateUrl: './datetime-picker.component.html',
  styleUrls: ['./datetime-picker.component.css']
})
export class DatetimePickerComponent implements OnInit {
  constructor(private readonly calendar: NgbCalendar) {}

  @Input() isVisible: boolean = false;
  @Input() position: { top?: number; left?: number; bottom?: number; right?: number } | null = null;
  @Input() minValidDate: NgbDate | null = null;
  @Input() minValidTime: string | null = null; // Format: HH:mm
  @Input() maxValidDate: NgbDate | null = null;
  @Input() maxValidTime: string | null = null; // Format: HH:mm
  
  @Output() isVisibleChange = new EventEmitter<boolean>();
  @Output() closed = new EventEmitter<void>();
  @Output() apply = new EventEmitter<DateTimeSelection>();

  selectedDate: NgbDate | null = null;
  selectedTime: string = '';
  selectedHour: number = 12;
  selectedMinute: number = 0;
  selectedPeriod: 'AM' | 'PM' = 'AM';
  minDate!: NgbDate;
  maxDate!: NgbDate;

  ngOnInit(): void {
    const today = this.calendar.getToday();
    // Use provided min/max dates or default values
    this.minDate = this.minValidDate || today;
    // If no maxValidDate provided, set to far future (no limit)
    this.maxDate = this.maxValidDate || this.calendar.getNext(today, 'y', 100);
    this.selectedDate = today;
    
    // Set default time to current time
    const now = new Date();
    const hours = now.getHours();
    this.selectedHour = hours > 12 ? hours - 12 : (hours === 0 ? 12 : hours);
    this.selectedMinute = now.getMinutes();
    this.selectedPeriod = hours >= 12 ? 'PM' : 'AM';
    this.updateTimeString();
  }

  close(): void {
    this.isVisible = false;
    this.isVisibleChange.emit(false);
    this.closed.emit();
  }

  handleBackdropClick(event: MouseEvent): void {
    // Always close when clicking on backdrop (outside the dialog)
    if (event.target === event.currentTarget) {
      this.close();
    }
  }

  handleApply(): void {
    if (this.isFormValid()) {
      this.updateTimeString();
      this.apply.emit({
        date: this.selectedDate!,
        time: this.selectedTime,
        dateTime: this.getSelectedDateTime()
      });
      this.close();
    }
  }

  setPeriod(period: 'AM' | 'PM'): void {
    this.selectedPeriod = period;
    this.updateTimeString();
  }

  updateTimeString(): void {
    let hour24 = this.selectedHour;
    if (this.selectedPeriod === 'PM' && this.selectedHour !== 12) {
      hour24 = this.selectedHour + 12;
    } else if (this.selectedPeriod === 'AM' && this.selectedHour === 12) {
      hour24 = 0;
    }
    this.selectedTime = `${String(hour24).padStart(2, '0')}:${String(this.selectedMinute).padStart(2, '0')}`;
  }

  handleCancel(): void {
    this.close();
  }

  onDateSelect(date: NgbDate): void {
    this.selectedDate = date;
  }

  isFormValid(): boolean {
    if (!this.selectedDate || this.selectedHour < 1 || this.selectedHour > 12 || this.selectedMinute < 0 || this.selectedMinute > 59) {
      return false;
    }
    
    // Validate against min/max dates with time consideration
    const selectedDateTime = this.getSelectedDateTime();
    
    // Check minimum datetime
    if (this.minValidDate) {
      const minTime = this.minValidTime ? this.parseTime(this.minValidTime) : { hour: 0, minute: 0 };
      const minDateTime = this.getDateTimeFromNgbDate(this.minValidDate, minTime.hour, minTime.minute);
      if (selectedDateTime < minDateTime) {
        return false;
      }
    }
    
    // Check maximum datetime (only if maxValidDate is provided)
    if (this.maxValidDate) {
      const maxTime = this.maxValidTime ? this.parseTime(this.maxValidTime) : { hour: 23, minute: 59 };
      const maxDateTime = this.getDateTimeFromNgbDate(this.maxValidDate, maxTime.hour, maxTime.minute);
      if (selectedDateTime > maxDateTime) {
        return false;
      }
    }
    
    return true;
  }

  private parseTime(timeString: string): { hour: number; minute: number } {
    const [hour, minute] = timeString.split(':').map(Number);
    return { hour, minute };
  }

  private getSelectedDateTime(): Date {
    if (!this.selectedDate) return new Date();
    
    let hour24 = this.selectedHour;
    if (this.selectedPeriod === 'PM' && this.selectedHour !== 12) {
      hour24 = this.selectedHour + 12;
    } else if (this.selectedPeriod === 'AM' && this.selectedHour === 12) {
      hour24 = 0;
    }
    
    return new Date(this.selectedDate.year, this.selectedDate.month - 1, this.selectedDate.day, hour24, this.selectedMinute);
  }

  private getDateTimeFromNgbDate(date: NgbDate, hour: number, minute: number): Date {
    return new Date(date.year, date.month - 1, date.day, hour, minute);
  }

  onHourChange(): void {
    if (this.selectedHour < 1) this.selectedHour = 1;
    if (this.selectedHour > 12) this.selectedHour = 12;
    this.updateTimeString();
  }

  onMinuteChange(): void {
    if (this.selectedMinute < 0) this.selectedMinute = 0;
    if (this.selectedMinute > 59) this.selectedMinute = 59;
    this.updateTimeString();
  }

  get formattedDate(): string {
    if (!this.selectedDate) return '';
    return `${this.selectedDate.year}-${String(this.selectedDate.month).padStart(2, '0')}-${String(this.selectedDate.day).padStart(2, '0')}`;
  }
}
